import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { PhoneCallingRoundedBold } from 'solar-icons';
import { Button, TextField } from '@/DesignSystem/Component';
import { LOGIN_ERRORS } from '@/Module/Auth/Constant';
import { isRubikaUser, isShadUser } from '@/Module/Auth/Helper';
import { authSendOtpCodeAction } from '@/Module/Auth/Store/Action';
import { authSetIsTransitioningAction, authSetPhoneNumber } from '@/Module/Auth/Store/Slice/AuthSlice';
import type { IUseOTP } from '@/Module/Comment/Hook/useOTP';
import { LINKS } from '@/Module/Core/Constant';
import { enqueueError, yup } from '@/Module/Core/Helper';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
interface IForm {
  mobile: string;
}
interface IAuthLoginForm {
  isModal?: boolean;
  setShowConfirmOTP?: IUseOTP['setShowConfirmOTP'];
}
const schema = yup.object({
  mobile: yup.string().isPersianMobile().required()
});
const FAST_LOGIN = true;
const AuthLoginForm: FC<IAuthLoginForm> = ({
  isModal = false,
  setShowConfirmOTP
}) => {
  const router = useRouter();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const dispatch = useAppDispatch();
  const redirectUrl = router.query?.redirectUrl ?? '';
  const isLoading = useAppSelector(state => state.auth.isLoading);
  const isTransitioning = useAppSelector(state => state.auth.isTransitioning);
  const {
    control,
    handleSubmit
  } = useForm<IForm>({
    resolver: yupResolver(schema)
  });
  const tryFastLogin = async () => {
    if (!FAST_LOGIN || process.env.NODE_ENV !== 'development' || isRubikaUser() || isShadUser()) return false;
    try {
      const {
        fastLogin
      } = await import('@/Module/Core/Helper/fastLogin').catch(() => ({
        fastLogin: null
      }));
      if (fastLogin) {
        await fastLogin();
        enqueueSnackbar({
          variant: 'success',
          message: 'ورود سریع انجام شد!'
        });
        return true;
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: 'خطا هنگام ورود سریع!'
      });
    }
    return false;
  };
  const onSubmit = useCallback(handleSubmit(async ({
    mobile
  }) => {
    dispatch(authSetIsTransitioningAction(true));

    // Fast Login
    const fastLoginDone = await tryFastLogin();
    if (fastLoginDone) return;

    // Normal OTP flow
    dispatch(authSendOtpCodeAction(mobile)).unwrap().then(() => {
      if (isModal && setShowConfirmOTP) {
        setShowConfirmOTP();
        dispatch(authSetPhoneNumber(mobile));
      } else {
        router.push({
          pathname: LINKS.AUTH.CONFIRM,
          query: {
            redirectUrl,
            mobile
          }
        });
      }
    }).catch(error => {
      const msg = LOGIN_ERRORS[error?.data?.error?.code?.toUpperCase()] || 'خطایی رخ داده است!';
      enqueueError(msg);
    });
  }, () => {
    dispatch(authSetIsTransitioningAction(false));
    enqueueSnackbar({
      variant: 'error',
      message: 'شماره موبایل را به صورت صحیح وارد کنید!'
    });
  }), [dispatch, enqueueSnackbar, isModal, redirectUrl, router, setShowConfirmOTP]);
  useEffect(() => {
    dispatch(authSetIsTransitioningAction(false));
  }, [dispatch]);
  return <form onSubmit={onSubmit} className="flex flex-1 flex-col" data-sentry-component="AuthLoginForm" data-sentry-source-file="AuthLoginForm.tsx">
      <TextField control={control} disabled={isLoading || isTransitioning} name="mobile" type="tel" label="شمارۀ همراه" prefix={<PhoneCallingRoundedBold className="felx mr-3 mt-3 items-center justify-center" />} autoComplete="off" className="mb-4 " autoFocus data-sentry-element="TextField" data-sentry-source-file="AuthLoginForm.tsx" />
      <div className="flex flex-1 flex-col justify-end ">
        <Button id="login" disabled={isLoading || isTransitioning} bold className="relative mt-4 w-full" variant="shadow" color="primary" radius="circle" size="large" type="submit" data-sentry-element="Button" data-sentry-source-file="AuthLoginForm.tsx">
          ورود | ثبت نام
        </Button>
      </div>
    </form>;
};
export default AuthLoginForm;